import { Injectable } from '@angular/core';
import { Case, CaseOption } from '../models/case';
import {
  sortCaseActions,
  CaseActionWithRelations,
  ActionPrerequisite,
} from '../utils/sort-case-actions.util';

import { DatabaseService } from './base/database.service';
import { Questionnaire } from '../models/questionnaire.model';
import { Faq } from '../models/faq';
import { Contract } from '../models/contract';
import { News } from '../mock/news';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CaseService extends DatabaseService<Case> {
  constructor(private http: HttpClient, private us: UserService) {
    super('cp_case');
    this.lambdaNames = { update: 'update_case' };
  }

  async getByCode(code: string | null) {
    return (
      await this.supabase
        .from(this.table)
        .select('*')
        .filter('code', 'eq', code)
    ).data![0] as Case;
  }

  override getAll(limit: number, page: number): Promise<Case[]> {
    return super.getAll(limit, page) as Promise<Case[]>;
  }

  async getActiveCases() {
    return (
      await this.supabase
        .from(this.table)
        .select('*')
        .filter('status', 'eq', 'ACTIVE')
    ).data as Case[];
  }

  /**
   * Casos ativos só com id, nome e código, para preencher um seletor. `getActiveCases` devolve a
   * linha inteira (configuration, descrições, requirements…), pesada demais para essa finalidade.
   */
  async getActiveCaseOptions(): Promise<CaseOption[]> {
    const { data, error } = await this.supabase
      .from(this.table)
      .select('id, summary, code')
      .eq('status', 'ACTIVE')
      .order('summary', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as CaseOption[];
  }

  /**
   * Nome e código de casos que já se conhecem pelo id (o caso de uma campanha, mesmo que tenha sido
   * desativado depois). Sem ids não consulta nada; ids repetidos viram uma só linha no filtro.
   */
  async getCaseOptionsByIds(ids: string[]): Promise<CaseOption[]> {
    const unique = [...new Set(ids)];
    if (!unique.length) return [];
    const { data, error } = await this.supabase
      .from(this.table)
      .select('id, summary, code')
      .in('id', unique);
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as CaseOption[];
  }

  async createCase(newcase: FullNewCase, image: Buffer) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;
    console.log('TOKEN', token);

    const ret = (await firstValueFrom(
      this.http.post(environment.lambdaUrl + '/generate_case', newcase, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    )) as any;

    const createdCase = ret.message.case.data[0];
    if (!createdCase.configuration) {
      createdCase.configuration = {};
    }
    await this.uploadImageToCase(image, `${createdCase.id}/coverImage.jpg`);

    const link = (await this.getCaseImage(createdCase.id)).data?.signedUrl;

    createdCase.configuration.images = {
      high_res: link,
    };

    console.log('CaseService -> createdCase', createdCase);

    const update = this.update(createdCase);
  }

  uploadImageToCase(image: Buffer, path: string) {
    return this.supabase.storage
      .from(environment.CASE_FILES_BUCKET || 'cases')
      .upload(path, image, { contentType: 'image/jpeg' });
  }

  getCaseImage(id: string) {
    return this.supabase.storage
      .from(environment.CASE_FILES_BUCKET || 'cases')
      .createSignedUrl(`${id}/coverImage.jpg`, 1000 * 60 * 60 * 24 * 365 * 10, {
        download: true,
      });
  }

  async getRequirementsForCase(id:string) {
    const ret = await this.supabase
      .from('cp_case_actions')
      .select('*,cp_actions:action_id!inner(*)')
      .eq('case_id', id);
    if (ret.error) {

      console.log(ret.error)
      throw new Error("Could not get case requirements.")
    }
    return ret.data;
  }

  async getRequirementsForCaseSorted(id: string) {
    const caseActions = await this.getRequirementsForCase(id);
    const caseActionIds = caseActions.map((ca: any) => ca.id);

    let prerequisites: ActionPrerequisite[] = [];
    if (caseActionIds.length > 0) {
      const { data: prereqsData } = await this.supabase
        .from('cp_action_prerequisites')
        .select('id, action_id, prereq_id')
        .in('action_id', caseActionIds)
        .in('prereq_id', caseActionIds);
      prerequisites = (prereqsData || []) as ActionPrerequisite[];
    }

    const sortedIds = sortCaseActions(caseActions as unknown as CaseActionWithRelations[], prerequisites);
    return sortedIds.map((sid) => caseActions.find((ca: any) => ca.id === sid)!);
  }

  async getDistinctSourceDisplayValues(caseSummary: string, cocounselUserId: string): Promise<string[]> {
    const { data, error } = await this.supabase
      .from('cpv_cocounsel_clients_2')
      .select('source_display')
      .eq('case_summary', caseSummary)
      .eq('cocounsel_user_id', cocounselUserId);
    if (error) {
      console.log(error);
      return [];
    }
    const values = (data ?? []).map((r: any) => r.source_display).filter((v: unknown): v is string => !!v);
    return [...new Set(values)].sort();
  }

  // Scoped by case_id (whole case), not cocounsel_user_id — used by the ADMINISTRATOR-side claims
  // table, which has no cocounsel scope. Queries x_clients_overview_v1, not cpv_cocounsel_clients_2.
  async getDistinctSourceOptionsForCase(caseId: string): Promise<string[]> {
    // x_clients_overview_v1 has no PostgREST grant anymore (it ran with
    // security_invoker off and bypassed RLS) — served by clients_overview_list.
    try {
      const res = await this.callLambda('clients_overview_list', 'POST', {
        mode: 'distinct',
        column: 'Source',
        caseId,
        limit: 5000,
      });
      const body: any = res?.details ?? res ?? {};
      const rows: any[] = body.data ?? [];
      const values = rows.map((r: any) => r['Source']).filter((v: unknown): v is string => !!v);
      return [...new Set(values)].sort();
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  /**
   * KYC actions dos cases onde o usuário tem claim, com o case de cada uma.
   *
   * CPK-5117: a lista era de TODOS os cases, e um KYC manual acabou gravado na action de outro
   * case — o PoA só reaproveita um KYC aprovado da action do próprio case, então disparava um
   * KYC novo por cima da aprovação. Sem claim, a lista volta vazia.
   */
  async getKycCaseActionsForUser(userId: string) {
    const claims = await this.supabase
      .from('cp_claim')
      .select('case_id, cp_claim_permission!inner(principal_id)')
      .eq('cp_claim_permission.principal_id', userId);

    if (claims.error) {
      console.log(claims.error);
      throw new Error("Could not get the user's claims.");
    }

    const caseIds = [
      ...new Set(
        (claims.data ?? [])
          .map((c: any) => c.case_id as string | null)
          .filter((id): id is string => !!id)
      ),
    ];
    if (!caseIds.length) return [];

    const ret = await this.supabase
      .from('cp_case_actions')
      .select(`
        *,
        cp_actions:action_id!inner(*),
        cp_case!case_id(
          id,
          summary,
          code
        )
      `)
      .eq('cp_actions.action_type', 'KYC')
      .in('case_id', caseIds);

    if (ret.error) {
      console.log(ret.error);
      throw new Error("Could not get KYC case actions.");
    }

    return ret.data;
  }
}

interface FullNewCase {
  case: Partial<Case>;
  questionnaire?: Partial<Questionnaire>;
  faq?: Partial<Faq>;
  contract?: Partial<Contract>;
  news?: Partial<News>;
}
